import { useEffect } from 'react';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';

/**
 * Mantém o input focado dentro de um Dialog visível quando o teclado abre no iOS/Android.
 * Aplicar em Dialogs com formulário, passando uma ref do DialogContent.
 *
 * Funciona junto com `useKeyboardScroll` global; este hook age só no dialog focado.
 */
export const useDialogKeyboardScroll = (active: boolean) => {
  useEffect(() => {
    if (!active || !Capacitor.isNativePlatform()) return;

    let showListener: PluginListenerHandle | null = null;
    let hideListener: PluginListenerHandle | null = null;
    const dialogSelector = '.MuiDialog-paper';

    const scrollFocusedIntoView = () => {
      const el = document.activeElement as HTMLElement | null;
      if (!el) return;
      const isField = el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
      if (!isField) return;
      setTimeout(() => {
        try {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch {}
      }, 180);
    };

    const getParts = () => {
      const activeEl = document.activeElement as HTMLElement | null;
      let paper = (activeEl?.closest(dialogSelector) as HTMLElement | null) || null;
      if (!paper) {
        const papers = document.querySelectorAll(dialogSelector);
        paper = (papers[papers.length - 1] as HTMLElement) || null;
      }
      const content = paper?.querySelector('.MuiDialogContent-root') as HTMLElement | null;
      return { paper, content };
    };

    const reset = () => {
      const { paper, content } = getParts();
      if (paper) paper.style.maxHeight = '';
      if (content) content.style.paddingBottom = '';
    };

    const setup = async () => {
      showListener = await Keyboard.addListener('keyboardDidShow', (info) => {
        const { paper, content } = getParts();
        if (paper) {
          // diminui altura do dialog para liberar espaço acima do teclado
          paper.style.maxHeight = `calc(100dvh - env(safe-area-inset-top) - ${info.keyboardHeight}px - 16px)`;
        }
        // garante espaço de rolagem abaixo do último campo para ele subir acima do teclado
        if (content) content.style.paddingBottom = `${info.keyboardHeight}px`;
        scrollFocusedIntoView();
      });
      hideListener = await Keyboard.addListener('keyboardWillHide', reset);
    };
    setup();

    return () => {
      showListener?.remove();
      hideListener?.remove();
      reset();
    };
  }, [active]);
};
